/**
 * Utility functions to generate unique, SEO-optimized meta descriptions
 * Each function creates dynamic descriptions based on entity data
 * All descriptions are under 160 characters for optimal SEO
 */

interface ListingData {
  name: string;
  type: 'gym' | 'store' | 'fitness_center' | string;
  city: string;
  rating?: number;
  short_description?: string;
  seo_description?: string;
  description?: string;
  address?: string;
  price_range?: string;
  tags?: string[];
  is_premium?: boolean;
}

interface EventData {
  title: string;
  event_type: string;
  city: string;
  date: string;
  short_description?: string;
  description: string;
  price?: number | null;
  max_participants?: number | null;
}

interface TrainerData {
  full_name: string;
  location_city: string;
  bio?: string;
  specialty?: string[];
  years_experience?: number;
  hourly_rate?: number;
  clients_trained?: number;
  certifications?: string[];
}

interface SportProfessionalData {
  full_name: string;
  category: string;
  ranking: number;
  location_country: string;
  location_city?: string;
  bio?: string;
  years_experience?: number;
  clients_trained?: number;
  achievements?: string[];
}

interface VideoData {
  titulo: string;
  descripcion?: string;
  categoria?: string;
  vistas?: number;
  votos_positivos?: number;
  creado_en?: string;
  ciudad?: string;
  duracion?: string;
}

interface ArticleData {
  title: string;
  meta_description?: string;
  summary?: string;
  tags?: string[];
  reading_time?: number;
  category?: string;
  votos_positivos?: number;
  publish_date?: string;
  created_at?: string;
}

/**
 * Generates unique meta descriptions for listings (gyms, stores, fitness centers)
 * Optimized for maximum CTR with urgency, benefits, and emotional triggers
 */
export const generateListingMetaDescription = (listing: ListingData): string => {
  const typeMap: Record<string, string> = {
    gym: 'Gimnasio',
    store: 'Tienda deportiva',
    fitness_center: 'Centro fitness'
  };
  
  const typeName = typeMap[listing.type] || 'Centro deportivo';
  
  // Use seo_description if available and well-crafted
  if (listing.seo_description && listing.seo_description.length > 50) {
    return listing.seo_description.substring(0, 160);
  }
  
  // Format 1: Premium with social proof (for premium listings)
  if (listing.is_premium && listing.rating) {
    return `✓ ${listing.name} ${listing.city} | ${typeName} TOP ${listing.rating}⭐ | ${listing.tags?.slice(0, 2).join(' + ') || 'Resultados garantizados'}. ${listing.price_range ? `Desde ${listing.price_range}.` : ''} ¡Prueba GRATIS hoy!`;
  }
  
  // Format 2: High rating with urgency
  if (listing.rating && listing.rating >= 4.5) {
    return `🔥 ${listing.name} ${listing.city} - ${listing.rating}/5 ⭐ por +100 clientes | ${typeName} líder. ${listing.tags?.slice(0, 2).join(', ') || 'Instalaciones premium'}. ${listing.address?.split(',')[0]}. ¡Reserva tu plaza!`;
  }
  
  // Format 3: Benefit-focused with tags
  if (listing.tags && listing.tags.length > 1) {
    return `💪 Transforma tu cuerpo en ${listing.name} (${listing.city}) | Expertos en ${listing.tags[0]} y ${listing.tags[1]}. ${listing.rating ? `${listing.rating}⭐ valorado.` : ''} ${listing.price_range || 'Primera clase GRATIS'} ¡Empieza YA!`;
  }
  
  // Format 4: Location + value proposition
  if (listing.address) {
    return `${typeName} ${listing.name} en ${listing.address.split(',')[0]}, ${listing.city} | ${listing.rating ? `${listing.rating}/5⭐` : 'Mejor valorado'} | Equipamiento profesional + clases incluidas. ${listing.price_range ? `Desde ${listing.price_range}` : '¡Ven hoy!'} Sin permanencia`;
  }
  
  // Format 5: Short description enhanced
  if (listing.short_description) {
    const cleanDesc = listing.short_description.substring(0, 85);
    return `${listing.name} ${listing.city}: ${cleanDesc} | ${listing.rating ? `⭐${listing.rating}/5` : 'Instalaciones top'} | ${listing.tags?.[0] || 'Entrenamiento personalizado'}. ¡Visita gratis!`;
  }
  
  // Format 6: Generic with strong CTA
  return `${listing.name} - El mejor ${typeName.toLowerCase()} de ${listing.city} ${listing.rating ? `(${listing.rating}⭐)` : ''} | Instalaciones modernas, resultados reales. ${listing.price_range || 'Precios inmejorables'}. ¡Primera sesión GRATIS!`;
};

/**
 * Generates unique meta descriptions for events
 * Optimized for urgency and FOMO to maximize registrations
 */
export const generateEventMetaDescription = (event: EventData): string => {
  const eventDate = new Date(event.date);
  const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const dateStr = `${eventDate.getDate()} ${monthNames[eventDate.getMonth()]}`;
  const daysUntil = Math.ceil((eventDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  
  // Use short_description if compelling
  if (event.short_description && event.short_description.length > 50 && event.short_description.length <= 160) {
    return event.short_description;
  }
  
  // Format 1: Free event with urgency
  if (event.price === 0 || event.price === null) {
    return `🎯 GRATIS: ${event.title} ${event.city} (${dateStr}) | ${event.event_type} ${event.max_participants ? `| Solo ${event.max_participants} plazas` : '| Plazas limitadas'} ${daysUntil <= 7 ? `| ¡${daysUntil} días!` : ''} Inscripción AHORA`;
  }
  
  // Format 2: Paid event with scarcity
  if (event.price > 0 && event.max_participants) {
    return `⚡ ${event.title} ${event.city} ${dateStr} | ${event.event_type} ${event.price}€ | QUEDAN ${event.max_participants} plazas ${daysUntil <= 10 ? `| ¡Solo ${daysUntil} días!` : ''} Reserva AHORA o pierde tu oportunidad`;
  }
  
  // Format 3: Price-focused with value
  if (event.price > 0) {
    return `🏆 ${event.title} - ${event.event_type} TOP en ${event.city} (${dateStr}) | Desde ${event.price}€ ${daysUntil <= 14 ? `| Faltan ${daysUntil} días` : ''} | Inscripción online. ¡No te quedes fuera!`;
  }
  
  // Format 4: Description with urgency
  const cleanDesc = event.description.replace(/<[^>]*>/g, '').substring(0, 90);
  return `${event.title} ${event.city} ${dateStr}: ${cleanDesc} ${event.max_participants ? `| ${event.max_participants} plazas` : ''} ${daysUntil <= 7 ? '¡ÚLTIMA SEMANA!' : '¡Apúntate!'}`;
};

/**
 * Generates unique meta descriptions for trainer profiles
 * Emphasizes results, credibility, and clear value proposition
 */
export const generateTrainerMetaDescription = (trainer: TrainerData): string => {
  // Format 1: Results-focused with social proof
  if (trainer.clients_trained && trainer.clients_trained > 50 && trainer.specialty && trainer.specialty.length > 0) {
    return `💪 ${trainer.full_name} - Entrenador TOP ${trainer.location_city} | +${trainer.clients_trained} clientes transformados | Experto ${trainer.specialty[0]} ${trainer.years_experience ? `| ${trainer.years_experience} años` : ''} ${trainer.hourly_rate ? `| ${trainer.hourly_rate}€/h` : ''} 1ª sesión GRATIS`;
  }
  
  // Format 2: Certification + specialty authority
  if (trainer.certifications && trainer.certifications.length >= 2 && trainer.specialty && trainer.specialty.length > 0) {
    return `✓ ${trainer.full_name} ${trainer.location_city}: ${trainer.certifications.length} certificaciones oficiales | Especialista ${trainer.specialty.slice(0, 2).join(' + ')} ${trainer.hourly_rate ? `| Desde ${trainer.hourly_rate}€/sesión` : ''} | Resultados garantizados. ¡Reserva!`;
  }
  
  // Format 3: Experience with transformation promise
  if (trainer.years_experience && trainer.years_experience >= 5 && trainer.specialty) {
    return `🎯 ${trainer.full_name} - ${trainer.years_experience} años transformando cuerpos en ${trainer.location_city} | ${trainer.specialty[0]} + ${trainer.specialty[1] || 'Nutrición'} ${trainer.hourly_rate ? `| ${trainer.hourly_rate}€/h` : ''} ${trainer.clients_trained ? `| +${trainer.clients_trained} éxitos` : ''} ¡Empieza HOY!`;
  }
  
  // Format 4: Bio with clear benefit
  if (trainer.bio && trainer.specialty) {
    const cleanBio = trainer.bio.substring(0, 70);
    return `${trainer.full_name} ${trainer.location_city}: ${cleanBio} | ${trainer.specialty[0]} profesional ${trainer.hourly_rate ? `| Desde ${trainer.hourly_rate}€` : ''} ${trainer.years_experience ? `| ${trainer.years_experience} años exp` : ''} Primera clase GRATIS`;
  }
  
  // Format 5: Value proposition focused
  return `${trainer.full_name} - Tu entrenador personal certificado en ${trainer.location_city} ${trainer.specialty?.[0] ? `| Especialista ${trainer.specialty[0]}` : ''} ${trainer.hourly_rate ? `| ${trainer.hourly_rate}€/sesión` : ''} | Resultados reales. ¡Prueba sin compromiso!`;
};

/**
 * Generates unique meta descriptions for sport professionals
 * Emphasizes achievements, ranking authority, and inspirational messaging
 */
export const generateSportProfessionalMetaDescription = (athlete: SportProfessionalData): string => {
  const categoryName = athlete.category
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  // Format 1: Top 3 with major achievements
  if (athlete.ranking <= 3 && athlete.achievements && athlete.achievements.length > 0) {
    return `🏆 ${athlete.full_name} - #${athlete.ranking} ${categoryName} ${athlete.location_country} | ${athlete.achievements[0].substring(0, 65)} ${athlete.years_experience ? `| ${athlete.years_experience} años élite` : ''} | Biografía completa`;
  }
  
  // Format 2: Bio with social proof
  if (athlete.bio && athlete.clients_trained && athlete.clients_trained > 20) {
    return `⭐ ${athlete.full_name} (#${athlete.ranking} ${categoryName}) ${athlete.location_city || athlete.location_country}: ${athlete.bio.substring(0, 75)} | +${athlete.clients_trained} atletas formados ${athlete.years_experience ? `| ${athlete.years_experience} años pro` : ''} ¡Conoce más!`;
  }
  
  // Format 3: Multiple achievements showcase
  if (athlete.achievements && athlete.achievements.length >= 2) {
    return `🥇 ${athlete.full_name} - ${categoryName} TOP ${athlete.ranking} | Palmarés: ${athlete.achievements.slice(0, 2).join(' + ').substring(0, 90)} ${athlete.location_country} ${athlete.years_experience ? `| ${athlete.years_experience}a carrera` : ''} Ver trayectoria`;
  }
  
  // Format 4: Experience with training authority
  if (athlete.years_experience && athlete.years_experience >= 10) {
    return `${athlete.full_name}: ${athlete.years_experience} años como profesional de ${categoryName} (#${athlete.ranking} ${athlete.location_country}) ${athlete.clients_trained ? `| Formó a +${athlete.clients_trained} deportistas` : ''} ${athlete.location_city ? `| ${athlete.location_city}` : ''} ¡Inspírate!`;
  }
  
  // Format 5: Ranking focus with call to action
  return `${athlete.full_name} - Profesional ${categoryName} #${athlete.ranking} ${athlete.location_country} ${athlete.bio?.substring(0, 80) || 'Carrera destacada y logros nacionales'} ${athlete.years_experience ? `| ${athlete.years_experience} años` : ''} Descubre su historia`;
};

/**
 * Generates unique meta descriptions for videos
 * Optimized for engagement with view counts, social proof, and clear value
 */
export const generateVideoMetaDescription = (video: VideoData): string => {
  // Use existing description if well-crafted
  if (video.descripcion && video.descripcion.length > 60 && video.descripcion.length <= 160) {
    return video.descripcion;
  }
  
  const categoria = video.categoria || 'Fitness';
  const vistas = video.vistas || 0;
  const likes = video.votos_positivos || 0;
  
  // Format 1: High engagement with social proof (for popular videos)
  if (vistas > 1000 && likes > 50) {
    return `🎥 ${video.titulo} | ${categoria} ${vistas > 10000 ? `+${Math.floor(vistas/1000)}K` : vistas} vistas ⭐ ${likes} likes ${video.ciudad ? `| ${video.ciudad}` : ''} ${video.duracion ? `| ${video.duracion}` : ''} ¡Aprende técnicas PRO GRATIS!`;
  }
  
  // Format 2: Social proof focused (for moderately popular)
  if (vistas > 500 || likes > 20) {
    return `▶️ ${video.titulo} - Tutorial ${categoria} ${vistas > 0 ? `| ${vistas} reproducciones` : ''} ${likes > 0 ? `| ${likes}👍` : ''} | Técnicas paso a paso explicadas. ${video.ciudad ? `Grabado en ${video.ciudad}` : ''} ¡Mira AHORA!`;
  }
  
  // Format 3: Category expertise with value prop
  if (video.categoria) {
    return `💪 ${video.titulo} | Guía completa ${categoria} ${video.duracion ? `(${video.duracion})` : ''} | Ejercicios efectivos + consejos pro ${video.ciudad ? `| ${video.ciudad}` : ''} ${vistas > 0 ? `| Ya vieron ${vistas}+` : ''} ¡Gratis y fácil!`;
  }
  
  // Format 4: Description-based enhanced
  if (video.descripcion) {
    const cleanDesc = video.descripcion.substring(0, 95);
    return `🔥 ${video.titulo}: ${cleanDesc} | ${categoria} ${vistas > 100 ? `| ${vistas} vistas` : ''} ${video.duracion ? `| ${video.duracion}` : ''} ¡Ver tutorial completo!`;
  }
  
  // Format 5: Generic with strong CTA
  return `${video.titulo} - Video tutorial ${categoria} ${video.duracion ? `(${video.duracion})` : ''} | Aprende técnicas profesionales paso a paso ${video.ciudad ? `en ${video.ciudad}` : ''} ${vistas > 0 ? `| ${vistas}+ vistas` : ''} ¡100% GRATIS!`;
};

/**
 * Generates unique meta descriptions for articles
 * Optimized for authority, engagement, and clear value proposition
 */
export const generateArticleMetaDescription = (article: ArticleData): string => {
  // Use existing meta_description if well-crafted
  if (article.meta_description && article.meta_description.length > 60 && article.meta_description.length <= 160) {
    return article.meta_description;
  }
  
  const readTime = article.reading_time || 5;
  const category = article.category || 'Fitness';
  const likes = article.votos_positivos || 0;
  const hasHighEngagement = likes > 20;
  
  // Format 1: High engagement with social proof
  if (hasHighEngagement && article.tags && article.tags.length > 0) {
    return `📖 ${article.title} | Guía completa ${category} (${readTime} min) ${likes}+ lectores lo avalan | ${article.tags.slice(0, 2).join(' + ')} | Consejos expertos verificados ¡Lee GRATIS!`;
  }
  
  // Format 2: Authority with reading time
  if (article.summary && readTime > 0) {
    const cleanSummary = article.summary.substring(0, 85);
    return `✓ ${article.title}: ${cleanSummary} | ${category} ${readTime} min lectura ${likes > 0 ? `| ${likes}👍` : ''} | Guía paso a paso. ¡Descubre más!`;
  }
  
  // Format 3: Tags with benefit focus
  if (article.tags && article.tags.length >= 2) {
    return `💡 ${article.title} | Todo sobre ${article.tags[0]} y ${article.tags[1]} ${readTime > 0 ? `| ${readTime} min` : ''} | Artículo ${category} ${hasHighEngagement ? `+ Top valorado` : 'actualizado'} ¡Aprende YA gratis!`;
  }
  
  // Format 4: Category expertise with clear value
  if (category && readTime) {
    return `🎯 ${article.title} - Artículo experto ${category} | ${readTime} minutos de lectura profesional ${likes > 10 ? `| Recomendado por ${likes}+ lectores` : ''} | Información verificada 100% GRATIS`;
  }
  
  // Format 5: Summary-based with CTA
  if (article.summary) {
    const cleanSummary = article.summary.substring(0, 110);
    return `${article.title}: ${cleanSummary} ${readTime ? `| ${readTime} min` : ''} ${likes > 0 ? `| ${likes} likes` : ''} ¡Lee el artículo completo!`;
  }
  
  // Format 6: Generic with strong value prop
  return `${article.title} | Artículo completo ${category} ${readTime ? `(${readTime} min lectura)` : ''} | Guía profesional con consejos prácticos y verificados ${likes > 5 ? `| +${likes} valoraciones` : ''} ¡100% GRATIS!`;
};

/**
 * Generates unique meta descriptions for landing pages
 * Each landing page gets a custom, benefit-focused description
 */
export const generateLandingPageMetaDescription = (pageType: string, city?: string): string => {
  const currentYear = new Date().getFullYear();
  
  const descriptions: Record<string, string> = {
    'yoga': `🧘 Llena tu centro de yoga ${currentYear}: +300 alumnos nuevos/año con perfil premium. Sistema completo de captación. Sin permanencia. ¡Prueba GRATIS 30 días + Setup incluido!`,
    
    'pilates': `💪 Duplica alumnos en tu estudio Pilates ${currentYear}: Perfil profesional + leads cualificados. Ya usan 200+ centros. Desde 29€/mes. ¡Primera clase gratis + Demo!`,
    
    'crossfit': `🔥 Llena tu Box CrossFit ${currentYear}: +150 atletas/año captados automáticamente. Sistema usado por 500+ boxes. Métricas reales. ¡Prueba GRATIS 14 días sin tarjeta!`,
    
    'artes-marciales': `🥋 +100 alumnos nuevos/año para tu Dojo ${currentYear}: Perfil premium + marketing automático. Sin permanencia. Desde 29€/mes. ¡1ª semana GRATIS + Soporte 24/7!`,
    
    'fitness': `💎 Multiplica x3 tus socios ${currentYear}: Plataforma líder para gimnasios. +500 centros generan leads diarios. Desde 49€/mes ¡Demo GRATIS ahora!`,
    
    'los-mejores': `🏆 TOP Deportistas España ${currentYear}: Vota por los mejores en +70 disciplinas. Ranking oficial verificado. Descubre campeones y stats reales. ¡Vota AHORA gratis!`,
    
    'entrenadores': `🎯 Encuentra TU entrenador perfecto ${currentYear}: +1000 profesionales certificados. Compara precios, lee 5000+ opiniones reales. Desde 25€/sesión. ¡1ª clase GRATIS!`,
    
    'nutricionistas': `🥗 Nutricionistas deportivos certificados ${currentYear}: +200 expertos en toda España. Planes personalizados, resultados reales. Compara precios y opiniones. ¡Consulta GRATIS!`,
    
    'fisioterapeutas': `🏥 Fisioterapeutas deportivos TOP ${currentYear}: +150 especialistas en lesiones. Recuperación rápida garantizada. Opiniones verificadas. ¡Primera consulta -20%!`,
    
    'eventos': `⚡ +500 eventos deportivos ${currentYear} España: Maratones, torneos, competiciones. Inscripción online fácil. Filtra por deporte, ciudad, fecha. ¡Encuentra tu reto!`,
    
    'videos': `🎥 Videos fitness y tutoriales ${currentYear}: +400 ejercicios explicados paso a paso. Rutinas profesionales GRATIS. Aprende técnicas de expertos. ¡Ver ahora!`,
    
    'articulos': `📖 Blog fitness y nutrición ${currentYear}: +500 artículos de expertos. Consejos verificados, guías completas. Entrenamiento, dietas, salud. ¡Lee gratis!`,
    
    'tiendas': `🛒 Tiendas deportivas España ${currentYear}: Equipamiento, ropa, suplementos. Compara precios y opiniones. Ofertas exclusivas. ¡Encuentra cerca de ti!`,
    
    'bienestar': `🧘‍♀️ Centros bienestar y spa ${currentYear}: +150 centros verificados. Masajes, terapias, wellness. 0% comisiones. Desde €49/mes. ¡Registra tu centro GRATIS!`,
    
    'directorio': `📍 Directorio Deportivo #1 España ${currentYear}: +2000 gimnasios, tiendas, entrenadores verificados. Opiniones reales. ¡Encuentra lo tuyo en 30 segundos!`,
    
    'cerca-de-mi': city 
      ? `📍 ${city} ${currentYear}: Gimnasios cerca de ti con precios reales. Compara +50 centros, lee opiniones, reserva online. Mapa en vivo. ¡Visita hoy!`
      : `📍 Encuentra tu gimnasio ideal ${currentYear}: Búsqueda geolocalizada. +2000 centros, opiniones verificadas, precios transparentes. ¡Descubre cerca de ti!`,
  };
  
  return descriptions[pageType] || `Ekselencia - Plataforma Deportiva #1 España ${currentYear}: +2000 gimnasios, entrenadores certificados, eventos deportivos. Todo en un clic. ¡Empieza AHORA gratis!`;
};

/**
 * Generates unique meta descriptions for nutritionist profiles
 */
interface NutritionistData {
  name: string;
  city: string;
  bio?: string;
  specializations?: string[];
  years_experience?: number;
  session_price?: number;
  certifications?: string[];
}

export const generateNutritionistMetaDescription = (nutritionist: NutritionistData): string => {
  const { name, city, bio, specializations, years_experience, session_price, certifications } = nutritionist;
  
  // Format 1: Specializations + experience
  if (specializations && specializations.length > 0 && years_experience) {
    return `🥗 ${name} - Nutricionista ${city} | ${years_experience} años exp | Especialista ${specializations.slice(0, 2).join(' + ')} ${session_price ? `| ${session_price}€/sesión` : ''} ${certifications?.length ? `| ${certifications.length} certificaciones` : ''} ¡Consulta GRATIS!`.substring(0, 160);
  }
  
  // Format 2: Certifications focused
  if (certifications && certifications.length >= 2) {
    return `✓ ${name} Nutricionista ${city}: ${certifications.length} certificaciones oficiales | ${specializations?.[0] || 'Nutrición deportiva'} ${years_experience ? `| ${years_experience} años` : ''} ${session_price ? `| Desde ${session_price}€` : ''} ¡Primera consulta -20%!`.substring(0, 160);
  }
  
  // Format 3: Bio-based
  if (bio) {
    const cleanBio = bio.substring(0, 70);
    return `${name} ${city}: ${cleanBio} | Nutricionista profesional ${session_price ? `| ${session_price}€/sesión` : ''} ${years_experience ? `| ${years_experience} años exp` : ''} ¡Consulta online disponible!`.substring(0, 160);
  }
  
  // Format 4: Generic
  return `${name} - Nutricionista profesional en ${city} | ${specializations?.[0] || 'Nutrición personalizada'} ${session_price ? `| Desde ${session_price}€` : ''} | Resultados garantizados. ¡Primera consulta GRATIS!`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for physiotherapist profiles
 */
interface PhysiotherapistData {
  name: string;
  city: string;
  bio?: string;
  specializations?: string[];
  years_experience?: number;
  session_price?: number;
  certifications?: string[];
}

export const generatePhysiotherapistMetaDescription = (physio: PhysiotherapistData): string => {
  const { name, city, bio, specializations, years_experience, session_price, certifications } = physio;
  
  // Format 1: Specializations + experience
  if (specializations && specializations.length > 0 && years_experience) {
    return `🏥 ${name} - Fisioterapeuta ${city} | ${years_experience} años exp | ${specializations.slice(0, 2).join(' + ')} ${session_price ? `| ${session_price}€/sesión` : ''} ${certifications?.length ? `| ${certifications.length} certificaciones` : ''} ¡Cita rápida!`.substring(0, 160);
  }
  
  // Format 2: Certifications focused
  if (certifications && certifications.length >= 2) {
    return `✓ ${name} Fisio ${city}: ${certifications.length} certificaciones | Experto ${specializations?.[0] || 'Lesiones deportivas'} ${years_experience ? `| ${years_experience} años` : ''} ${session_price ? `| Desde ${session_price}€` : ''} ¡Primera sesión -20%!`.substring(0, 160);
  }
  
  // Format 3: Bio-based
  if (bio) {
    const cleanBio = bio.substring(0, 70);
    return `${name} ${city}: ${cleanBio} | Fisioterapeuta deportivo ${session_price ? `| ${session_price}€/sesión` : ''} ${years_experience ? `| ${years_experience} años exp` : ''} ¡Recuperación garantizada!`.substring(0, 160);
  }
  
  // Format 4: Generic
  return `${name} - Fisioterapeuta profesional en ${city} | ${specializations?.[0] || 'Rehabilitación deportiva'} ${session_price ? `| Desde ${session_price}€` : ''} | Tratamiento personalizado. ¡Pide cita HOY!`.substring(0, 160);
};

/**
 * Truncates and cleans description to fit within character limit
 */
export const sanitizeMetaDescription = (description: string, maxLength: number = 160): string => {
  // Remove HTML tags
  let clean = description.replace(/<[^>]*>/g, '');
  
  // Remove extra whitespace
  clean = clean.replace(/\s+/g, ' ').trim();
  
  // Truncate if needed
  if (clean.length > maxLength) {
    clean = clean.substring(0, maxLength - 3) + '...';
  }
  
  return clean;
};

// Export all utility functions
export type { ListingData, EventData, TrainerData, SportProfessionalData, VideoData, ArticleData, NutritionistData, PhysiotherapistData };
